import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getAccount, getAccountAssets, postAccount, deleteAccount, postAccountAllocationRatio, postAccountReload } from '@entities/account'
import { postOrderPositions, postOrderOpen } from '@entities/order'
import { getAuthSession } from '@entities/auth'
import { applyAuthSession, resolveAuthSession, useAuthStore, useLogout } from '@features/auth'
import { isEmbeddedSignEnabled, setAccountStatus } from '@shared/utils'
import { useAlertStore } from '@shared/store'
import { useUserAccStore, selectAssets } from './userAccStore'

const UNMATCHED_MAX_POLL_COUNT = 3
const ACCOUNT_READY_MAX_POLL_COUNT = 10
const ACCOUNT_READY_POLL_INTERVAL = 30000
const ELECTRONIC_SIGN_TRADE_TYPE = '2'

interface AccountReadyState {
  isReady: boolean
  code:
    | 'no_account'
    | 'unmatched'
    | 'vps_missing'
    | 'vps_preparing'
    | 'vps_error'
    | 'vps_stopped'
    | 'vps_deleted'
    | 'server_missing'
    | 'acc_con'
    | 'acc_mt5'
    | 'order_con'
    | 'order_mt5'
    | 'ready'
  message: string
  maxPollCount: number
}

const getMt5StatusMessage = (status: number, target: string) => {
  const detail =
    {
      [-1]: '계정 설정 오류',
      [-2]: '계좌가 로그인되지 않은 상태',
      [-3]: '계좌 불일치',
      [-4]: '계좌 서버 정보 없음',
      [-5]: '거래 불가 계좌',
      [-9]: '터미널 서버 연결 안됨',
    }[status] ?? '상태 확인중'

  return `${target} MT5 상태가 정상이지 않습니다. (${detail})`
}

export const getAccountReadyState = (
  account: TradingAccount | null | undefined,
): AccountReadyState => {
  if (!account) {
    return {
      isReady: false,
      code: 'no_account',
      message: '어카운트를 등록해 주세요.',
      maxPollCount: 0,
    }
  }
  if (account.matched === false) {
    return {
      isReady: false,
      code: 'unmatched',
      message: '관리자 미허가 계정입니다. 관리자에게 문의해주세요',
      maxPollCount: UNMATCHED_MAX_POLL_COUNT,
    }
  }
  if (!account.vps) {
    return {
      isReady: false,
      code: 'vps_missing',
      message: 'VPS 설치 상태를 확인 중입니다.',
      maxPollCount: ACCOUNT_READY_MAX_POLL_COUNT,
    }
  }
  if (account.vps.runStatus === -1) {
    return {
      isReady: false,
      code: 'vps_preparing',
      message: 'VPS 설치 준비중입니다.',
      maxPollCount: ACCOUNT_READY_MAX_POLL_COUNT,
    }
  }
  if (account.vps.runStatus === 9 || account.vps.runStatus !== 1) {
    return {
      isReady: false,
      code: 'vps_error',
      message: 'VPS 설정 또는 오류로 설치가 완료되지 않았습니다.',
      maxPollCount: ACCOUNT_READY_MAX_POLL_COUNT,
    }
  }
  if (account.vps.stopStatus === 9) {
    return {
      isReady: false,
      code: 'vps_stopped',
      message: 'VPS가 설정에 의해 정지되었습니다.',
      maxPollCount: ACCOUNT_READY_MAX_POLL_COUNT,
    }
  }
  if (account.vps.stopStatus === 99) {
    return {
      isReady: false,
      code: 'vps_deleted',
      message: 'VPS가 삭제된 상태입니다.',
      maxPollCount: ACCOUNT_READY_MAX_POLL_COUNT,
    }
  }
  if (!account.server) {
    return {
      isReady: false,
      code: 'server_missing',
      message: 'EA 런타임 상태를 확인 중입니다.',
      maxPollCount: ACCOUNT_READY_MAX_POLL_COUNT,
    }
  }
  if (account.server.accConStatus !== 1) {
    return {
      isReady: false,
      code: 'acc_con',
      message: '계좌 콜렉터 서버와 연결되지 않았습니다.',
      maxPollCount: ACCOUNT_READY_MAX_POLL_COUNT,
    }
  }
  if (account.server.accMt5Status !== 1) {
    return {
      isReady: false,
      code: 'acc_mt5',
      message: getMt5StatusMessage(account.server.accMt5Status, '계좌'),
      maxPollCount: ACCOUNT_READY_MAX_POLL_COUNT,
    }
  }
  if (account.server.orderConStatus !== 1) {
    return {
      isReady: false,
      code: 'order_con',
      message: '주문 서버와 연결되지 않았습니다.',
      maxPollCount: ACCOUNT_READY_MAX_POLL_COUNT,
    }
  }
  if (account.server.orderMt5Status !== 1) {
    return {
      isReady: false,
      code: 'order_mt5',
      message: getMt5StatusMessage(account.server.orderMt5Status, '주문'),
      maxPollCount: ACCOUNT_READY_MAX_POLL_COUNT,
    }
  }

  return {
    isReady: true,
    code: 'ready',
    message: '서버 연결이 완료되었습니다.',
    maxPollCount: 0,
  }
}

export const resolveAccountTradeType = (
  data: AccountListResponse | null | undefined,
): number | string | null => data?.tradeType ?? data?.accounts?.[0]?.tradeType ?? null

export const canShowEmbeddedSignMenu = ({
  tradeType,
}: {
  hasSavedAccount: boolean
  signed: boolean
  tradeType: number | string | null | undefined
}) => isEmbeddedSignEnabled() && String(tradeType ?? '') === ELECTRONIC_SIGN_TRADE_TYPE

export const useAccountAssets = () => {
  const svrId = useAuthStore(s => s.svrId)
  const { accountReadyState, isLoading: isAccountLoading } = useAccountList({ enabled: !!svrId })
  const isAccountActive = accountReadyState.isReady
  const { data, isLoading } = useQuery({
    queryKey: ['account', 'assets'],
    queryFn: getAccountAssets,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: !!svrId && isAccountActive,
  })

  // REST 응답을 restAssets 슬롯에 보관. WS는 wsAssets 슬롯에 별도 보관됨
  useEffect(() => {
    if (data?.assets) {
      useUserAccStore.getState().setFromRestAssets(data.assets)
    }
  }, [data])

  // selector가 wsAssets ?? restAssets 머지해서 반환
  const assets = useUserAccStore(selectAssets)

  return {
    balance: assets?.balance ?? null,
    freeMargin: assets?.freeMargin ?? null,
    equity: assets?.equity ?? null,
    profit: assets?.profit ?? null,
    isLoading: isAccountLoading || (isAccountActive && isLoading),
  }
}

// 계좌 목록 조회 — 읽기 전용 리더. 폴링/포커스 재조회를 하지 않는다.
// 가드·레이아웃·헤더·SocketProvider 등 앱 전역 여러 곳에서 호출되므로,
// 여기에 refetchInterval/focus 핸들러를 두면 인스턴스 수만큼 요청이 중복된다.
// 폴링이 필요한 화면(AccountForm)은 useAccountListPolling을 쓴다.
export const useAccountList = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const isLoggedIn = useAuthStore(s => s.isLoggedIn)
  const { data, isLoading } = useQuery({
    queryKey: ['account', 'list'],
    queryFn: getAccount,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: isLoggedIn && enabled,
  })

  const accounts = data?.accounts ?? []
  const signed = data?.signed === true
  const tradeType = resolveAccountTradeType(data)
  const hasSavedAccount = accounts.length > 0
  const canShowEmbeddedSign = canShowEmbeddedSignMenu({
    hasSavedAccount,
    signed,
    tradeType,
  })
  const accountReadyState = useMemo(() => getAccountReadyState(accounts[0]), [accounts])
  // 현재 계좌의 자산 비율 (계좌 1개 기준 — 첫 항목 사용)
  const allocationRatio = accounts[0]?.allocationRatio ?? null

  // 접근 제어용 — 계좌 status(1=생성완료)를 localStorage에 보관. SvrGuard가 이 값으로 판정.
  // 조회 성공 시점에만 반영하고, 계좌가 없으면 status 제거.
  useEffect(() => {
    if (!data) return
    setAccountStatus(data.accounts?.[0]?.status)
  }, [data])

  return {
    accounts,
    signed,
    tradeType,
    canShowEmbeddedSign,
    accountReadyState,
    isAccountReady: accountReadyState.isReady,
    allocationRatio,
    isLoading,
  }
}

// 계좌 목록 조회 + 폴링 소유자. AccountForm(useAccountForm)에서만 호출한다.
// 미허가/준비중 계정일 때 refetchInterval로 재조회하고 재조회 횟수(pollCount)를 센다.
// 폴링·포커스 재조회 로직을 이 한 곳에만 두어 요청 중복(동시 다발 조회)을 막는다.
// AccountForm은 미허가/준비중 계정이 리다이렉트되는 화면이라, 폴링이 필요한 순간엔 항상 마운트돼 있다.
export const useAccountListPolling = () => {
  const isLoggedIn = useAuthStore(s => s.isLoggedIn)
  const pollCountRef = useRef(0)
  const pollKeyRef = useRef<string | null>(null)
  const [pollCount, setPollCount] = useState(0)
  const { data, isLoading, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['account', 'list'],
    queryFn: getAccount,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchInterval: query => {
      const readyState = getAccountReadyState(query.state.data?.accounts?.[0])
      if (readyState.isReady || readyState.maxPollCount <= 0) return false
      return pollCountRef.current < readyState.maxPollCount
        ? ACCOUNT_READY_POLL_INTERVAL
        : false
    },
    refetchIntervalInBackground: true,
    enabled: isLoggedIn,
  })

  const accounts = data?.accounts ?? []
  const signed = data?.signed === true
  const tradeType = resolveAccountTradeType(data)
  const hasSavedAccount = accounts.length > 0
  const canShowEmbeddedSign = canShowEmbeddedSignMenu({
    hasSavedAccount,
    signed,
    tradeType,
  })
  const accountReadyState = useMemo(() => getAccountReadyState(accounts[0]), [accounts])
  const isPollingStopped =
    !accountReadyState.isReady &&
    accountReadyState.maxPollCount > 0 &&
    pollCount >= accountReadyState.maxPollCount
  const allocationRatio = accounts[0]?.allocationRatio ?? null

  const refetchAccountList = () => {
    pollCountRef.current = 0
    setPollCount(0)
    return refetch()
  }

  // 폴링 카운트는 "데이터 변경"이 아니라 "실제 요청 완료(dataUpdatedAt)" 기준으로 센다.
  // structural sharing으로 응답이 이전과 동일하면 accounts 참조가 유지되므로,
  // 참조에 의존하면 미허가 계정처럼 상태가 안 바뀌는 경우 카운트가 멈춰 무한 폴링이 발생한다.
  useEffect(() => {
    if (!dataUpdatedAt) return
    const key = `${accounts[0]?.accountId ?? 'none'}:${accountReadyState.code}`
    if (accountReadyState.isReady || accountReadyState.maxPollCount <= 0) {
      pollKeyRef.current = null
      pollCountRef.current = 0
      setPollCount(0)
      return
    }
    if (pollKeyRef.current !== key) {
      pollKeyRef.current = key
      pollCountRef.current = 1
      setPollCount(1)
      return
    }
    pollCountRef.current += 1
    setPollCount(pollCountRef.current)
  }, [dataUpdatedAt, accounts, accountReadyState])

  useEffect(() => {
    if (!isLoggedIn) return
    const handleResume = () => {
      if (document.visibilityState !== 'visible') return
      if (accountReadyState.code === 'unmatched' && isPollingStopped) return
      refetchAccountList()
    }

    document.addEventListener('visibilitychange', handleResume)
    window.addEventListener('focus', handleResume)
    return () => {
      document.removeEventListener('visibilitychange', handleResume)
      window.removeEventListener('focus', handleResume)
    }
  }, [accountReadyState.code, isLoggedIn, isPollingStopped])

  return {
    accounts,
    signed,
    tradeType,
    canShowEmbeddedSign,
    accountReadyState,
    isAccountReady: accountReadyState.isReady,
    isPollingStopped,
    pollCount,
    allocationRatio,
    isLoading,
    refetch: refetchAccountList,
  }
}

// 자산 비율 설정
export const useSetAllocationRatio = () => {
  const queryClient = useQueryClient()

  const { mutate: setAllocationRatio, isPending } = useMutation({
    mutationFn: (allocationRatio: number) =>
      postAccountAllocationRatio({ allocationRatio }),
    onMutate: async allocationRatio => {
      await queryClient.cancelQueries({ queryKey: ['account', 'list'] })
      const previous = queryClient.getQueryData<AccountListResponse>(['account', 'list'])

      queryClient.setQueryData<AccountListResponse>(['account', 'list'], old => {
        if (!old?.accounts?.length) return old
        return {
          ...old,
          accounts: old.accounts.map((account, index) =>
            index === 0 ? { ...account, allocationRatio } : account,
          ),
        }
      })

      return { previous }
    },
    onError: (_error, _allocationRatio, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['account', 'list'], context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'list'] })
    },
  })

  return { setAllocationRatio, isPending }
}

export const useRegisterAccount = () => {
  const queryClient = useQueryClient()

  const { mutate: registerAccount, isPending } = useMutation({
    mutationFn: postAccount,
    onSuccess: async () => {
      // 등록 후 서버 세션을 다시 읽어 accID(svrId)를 store에 동기화
      try {
        const session = resolveAuthSession(await getAuthSession())
        if (session) applyAuthSession(session)
      } catch {
        // 계좌 목록 refetch에서 최신 계좌 상태를 다시 맞춘다.
      }
      queryClient.invalidateQueries({ queryKey: ['account', 'list'] })
    },
  })

  return { registerAccount, isPending }
}

// 어카운트 리로드 — POST /api/v1/account/reload. 성공/실패 모두 알림으로 안내(별도 재조회 없음).
export const useReloadAccount = () => {
  const queryClient = useQueryClient()
  const { mutate: reloadAccount, isPending } = useMutation({
    mutationFn: postAccountReload,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'list'] })
      useAlertStore.getState().push('어카운트가 리로드되었습니다.')
    },
    onError: () => {
      useAlertStore.getState().push('리로드에 실패하였습니다. 다시 시도해 주세요.')
    },
  })

  return { reloadAccount, isPending }
}

export const usePositions = (enabled = true) => {
  const { data } = useQuery({
    queryKey: ['order', 'positions'],
    queryFn: postOrderPositions,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    enabled,
  })

  useEffect(() => {
    if (data?.positions) {
      useUserAccStore.getState().setFromRestPositions(data.positions)
    }
  }, [data])
}

export const useOpenOrders = (enabled = true) => {
  const { data } = useQuery({
    queryKey: ['order', 'openOrders'],
    queryFn: postOrderOpen,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    enabled,
  })

  useEffect(() => {
    if (data?.orders) {
      useUserAccStore.getState().setFromRestOpenOrders(data.orders)
    }
  }, [data])
}

export const useDeleteAccount = () => {
  const { logout } = useLogout()

  const { mutate: removeAccount, isPending } = useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      // 계좌 삭제 성공 → 로그아웃(isLoggedIn=false → 모든 소켓 cleanup, queryClient.clear, /login 이동)
      logout()
    },
  })

  return { removeAccount, isPending }
}
